import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Box,
  useMediaQuery,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import WelcomeComponent from "../../WelcomeComponent";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import { SplashScreen } from "../../SplashScreen";
import { getCurrentUserKycLink } from "../../../redux/userSlice";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const parseDateSafe = (dateString: string | Date | undefined): Date | null => {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;
  try {
    return new Date(dateString);
  } catch (error) {
    console.error("Error parsing date:", error);
    return null;
  }
};

const BankingView: React.FC = () => {
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const { isCompletingLogin } = useAppSelector((state) => state.ui);
  const { kycLink, isLoadingKycLink } = useAppSelector((state) => state.user);
  const onBoardingCompleted = useAppSelector(
    (state) => state.user.user?.onBoardingCompleted ?? false
  );
  const dispatch = useAppDispatch();

  const [kycStatus, setKycStatus] = useState<
    "loading" | "pending" | "in-progress" | "completed" | "not_started"
  >("loading");

  const { chainType } = useAppSelector((state) => state.wallet);

  // const parsedKycLink = useMemo(() => {
  //   if (!kycLink) return null;

  //   return {
  //     ...kycLink,
  //     // Only parse dates when you need to use them as Date objects
  //     _expiresAt: parseDateSafe(kycLink.expiresAt),
  //     _createdAt: parseDateSafe(kycLink.createdAt),
  //     _updatedAt: parseDateSafe(kycLink.updatedAt),
  //   };
  // }, [kycLink]);

  // Determine KYC status based on kycLink
  useEffect(() => {
    if (isLoadingKycLink) {
      setKycStatus("loading");
      return;
    }

    if (!kycLink) {
      setKycStatus("not_started");
      return;
    }

    if (
      kycLink.status === "not_started" ||
      kycLink.status === "pending" ||
      kycLink.status === "awaiting.applicant"
    ) {
      setKycStatus("in-progress");
    } else if (
      kycLink.status === "completed" ||
      kycLink.status === "approved"
    ) {
      setKycStatus("completed");
    } else {
      setKycStatus("pending");
    }
  }, [kycLink, isLoadingKycLink]);

  // Fetch KYC link only once when needed
  useEffect(() => {
    if (
      chainType !== "evm" &&
      !onBoardingCompleted &&
      !kycLink &&
      !isLoadingKycLink
    ) {
      dispatch(getCurrentUserKycLink());
    }
  }, [chainType, onBoardingCompleted, kycLink, isLoadingKycLink, dispatch]);

  const shouldShowKycFlow = useCallback(() => {
    return chainType !== "evm" && !onBoardingCompleted;
  }, [chainType, onBoardingCompleted]);

  const handleStartKyc = () => {
    if (kycLink && kycLink.url) {
      // Open Persona in a new tab or redirect
      window.open(kycLink.url, "_blank", "noopener,noreferrer");
      // Or redirect in the same window:
      // window.location.href = kycLink.url;
    }
  };

  const handleOpenTos = () => {
    if (kycLink && kycLink.tosUrl) {
      window.open(kycLink.tosUrl, "_blank", "noopener,noreferrer");
    }
  };

  const renderContent = useCallback(() => {
    // If user should see KYC flow
    if (shouldShowKycFlow()) {
      // Show loading state
      if (kycStatus === "loading") {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading KYC verification...</Typography>
          </Box>
        );
      }

      // If KYC link exists and needs to be started or is in progress
      if (kycLink && kycLink.url) {
        // Check if we should prompt user to start KYC
        const shouldShowKycPrompt =
          kycStatus === "pending" ||
          kycStatus === "in-progress" ||
          kycStatus === "not_started";

        if (shouldShowKycPrompt) {
          return (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Complete Identity Verification
              </Typography>
              <Typography variant="h6" gutterBottom>
                Identity Verification Required
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                To complete your account setup, you need to verify your
                identity. This process is handled by Persona, our identity
                verification partner.
              </Typography>

              {/* Terms of Service Section */}
              <Box sx={{ mb: 4, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Before proceeding, please review our Terms of Service
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  By proceeding with identity verification, you agree to our
                  Terms of Service.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenTos}
                  sx={{ mr: 2 }}
                >
                  View Terms of Service
                </Button>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  You must agree to the Terms of Service to continue
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleStartKyc}
                sx={{ mb: 2 }}
              >
                Start Identity Verification
              </Button>

              <Typography variant="body2" color="text.secondary">
                You will be redirected to Persona's secure verification
                platform.
                <br />
                Please complete all steps and return to this page.
              </Typography>
            </Box>
          );
        }
      }

      // If KYC is completed but onboarding not marked as completed
      if (kycStatus === "completed") {
        return (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="success.main" gutterBottom>
              KYC Verification Completed!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your identity verification has been completed successfully.
            </Typography>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Refresh to Continue
            </Button>
          </Box>
        );
      }

      // No KYC link available
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="warning.main" gutterBottom>
            KYC Setup Required
          </Typography>
          <Typography variant="body1">
            Please wait while we set up your identity verification process...
          </Typography>
          <CircularProgress sx={{ mt: 2 }} />
        </Box>
      );
    }

    // Default: show welcome component
    return <WelcomeComponent />;
  }, [shouldShowKycFlow, kycLink, kycStatus]);

  const renderHeader = () => (
    <>
      <Box sx={{ p: isMobileView ? 1 : 2, textAlign: "start" }}>
        <Typography
          variant="h4"
          sx={{ fontSize: isMobileView ? "1.5rem" : "2rem" }}
        >
          Banking
        </Typography>
        <Typography sx={{ fontSize: isMobileView ? "0.875rem" : "1rem" }}>
          Manage your banking settings and preferences.
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
    </>
  );

  // Poll for KYC completion when user might have completed it in another tab
  useEffect(() => {
    if (shouldShowKycFlow() && kycStatus === "in-progress") {
      const interval = setInterval(() => {
        dispatch(getCurrentUserKycLink());
      }, 20000); // Poll every 10 seconds

      return () => clearInterval(interval);
    }
  }, [shouldShowKycFlow, kycStatus, dispatch]);

  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        // Mobile-specific styles
        ...(isMobileView && {
          maxWidth: "100%",
          px: 1,
        }),
        // Desktop-specific styles
        ...(!isMobileView && {
          boxShadow: 1,
          borderRadius: 2,
        }),
      }}
    >
      <>
        {renderHeader()}
        {renderContent()}
      </>
    </Box>
  );
};

export default BankingView;
